import { Badge, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, Popover, Switch, Tooltip, Typography, useTheme } from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Icons } from '../shared/utils/icons.jsx'
import usePushNotifications from './usePushNotifications.js'
import { needsInstallForPush, promptAddToHomeScreen, registerPwaWorker } from './pwaInstall.js'

const StudentPushNotifications = ( { apiClient, currentUser } ) => {
    const navigate = useNavigate()
    const { palette } = useTheme()

    const push = usePushNotifications( {
        apiClient,
        audience: 'user'
    } )

    const [anchorEl, setAnchorEl] = useState( null )
    const [promptOpen, setPromptOpen] = useState( false )
    const [installDialogOpen, setInstallDialogOpen] = useState( false )
    // Pushes received while the user is actively on the page (sent by push-sw.js instead of a system notification)
    const [inAppNotifications, setInAppNotifications] = useState( [] )

    const activeNotification = inAppNotifications[0] || null
    const userId = currentUser?.AUID || currentUser?.user_id || currentUser?.empcode || currentUser?.email || ''
    const promptKey = userId ? `student-notification-prompt-${userId}` : ''

    // iOS Safari reports push as unsupported until the app is added to the home screen
    const installRequired = needsInstallForPush()
    const available = push.supported || installRequired

    // Makes the app installable (Add to Home Screen)
    useEffect( () => {
        registerPwaWorker()
    }, [] )

    useEffect( () => {
        if ( !promptKey || !available || push.permission === 'denied' || push.subscribed ) return
        if ( localStorage.getItem( promptKey ) ) return

        localStorage.setItem( promptKey, 'shown' )
        setPromptOpen( true )
    }, [promptKey, available, push.permission, push.subscribed] )

    // Converts the absolute URL from the service worker into an in-app route when it belongs to this site
    const goToUrl = useCallback( url => {
        if ( !url ) return

        const target = new URL( url, window.location.origin )
        if ( target.origin === window.location.origin ) {
            navigate( target.pathname + target.search + target.hash )
        } else {
            window.location.href = target.href
        }
    }, [navigate] )

    useEffect( () => {
        if ( typeof navigator === 'undefined' || !( 'serviceWorker' in navigator ) ) return

        const handleMessage = event => {
            const message = event.data
            if ( !message || typeof message !== 'object' ) return

            if ( message.type === 'placement-push-received' && message.notification ) {
                setInAppNotifications( previous => {
                    const incoming = message.notification
                    // Same tag replaces the older notification about the same thing (mirrors the system behaviour)
                    const rest = incoming.tag ? previous.filter( notification => notification.tag !== incoming.tag ) : previous
                    return [...rest, { ...incoming, id: `${Date.now()}-${Math.random()}` }]
                } )
            }

            if ( message.type === 'placement-push-navigate' ) {
                goToUrl( message.url )
            }
        }

        navigator.serviceWorker.addEventListener( 'message', handleMessage )
        navigator.serviceWorker.startMessages?.()

        return () => navigator.serviceWorker.removeEventListener( 'message', handleMessage )
    }, [goToUrl] )

    // iOS can't send push to a browser tab: install the app first. Returns true when the install flow took over.
    const handleInstallFirst = async () => {
        if ( !needsInstallForPush() ) return false

        setAnchorEl( null )
        setPromptOpen( false )

        // iOS has no install API, so this is 'unavailable' there and we show the manual steps
        const outcome = await promptAddToHomeScreen()
        if ( outcome === 'unavailable' ) setInstallDialogOpen( true )

        return true
    }

    const handleToggle = async event => {
        if ( await handleInstallFirst() ) return

        try {
            if ( event.target.checked ) {
                await push.enable()
            } else {
                await push.disable()
            }
        } catch ( err ) {
            console.error( err )
        }
    }

    const handleEnableFromPrompt = async () => {
        if ( await handleInstallFirst() ) return

        try {
            await push.enable()
        } catch ( err ) {
            console.error( err )
        } finally {
            setPromptOpen( false )
        }
    }

    const handleDismissInAppNotification = () => {
        setInAppNotifications( previous => previous.slice( 1 ) )
    }

    const handleViewInAppNotification = () => {
        const url = activeNotification?.url
        handleDismissInAppNotification()
        goToUrl( url )
    }

    return (
        <>
            <Tooltip title='Notifications'>
                <IconButton
                    size='small'
                    onClick={event => setAnchorEl( event.currentTarget )}
                    sx={{ color: 'inherit' }}
                >
                    <Badge color='error' variant='dot' invisible={push.subscribed || !available}>
                        {Icons.default.NotificationsOutlined}
                    </Badge>
                </IconButton>
            </Tooltip>

            <Popover
                open={Boolean( anchorEl )}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl( null )}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Box sx={{ width: 300, p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Typography variant='subtitle2' sx={{ fontWeight: 600 }}>Notifications</Typography>
                    <Typography variant='caption' color='text.secondary'>
                        Stay updated with placement alerts and class updates.
                    </Typography>

                    <FormControlLabel
                        labelPlacement='start'
                        label={<Typography variant='body2'>Enabled on this browser</Typography>}
                        control={
                            <Switch
                                size='small'
                                checked={push.subscribed}
                                onChange={handleToggle}
                                disabled={push.busy || !available}
                            />
                        }
                        sx={{ p: 1, borderRadius: 4, bgcolor: palette.contentBg, m: 0, justifyContent: 'space-between', border: `1px solid ${palette.primary.light}` }}
                    />

                    {!available && (
                        <Typography variant='caption' color='error.main'>
                            This browser does not support push notifications.
                        </Typography>
                    )}

                    {push.supported && push.permission === 'denied' && (
                        <Typography variant='caption' color='error.main'>
                            Notifications are blocked in your browser settings.
                        </Typography>
                    )}
                </Box>
            </Popover>

            <Dialog open={promptOpen} onClose={() => setPromptOpen( false )} maxWidth='xs' fullWidth>
                <DialogTitle sx={{ fontSize: '15px', fontWeight: 'bold', pb: 1 }}>Enable notifications</DialogTitle>
                <DialogContent sx={{ pb: 1 }}>
                    <Typography variant='body2' sx={{ fontSize: '13px', color: 'text.secondary', lineHeight: 1.5 }}>
                        Stay updated with placement alerts and class updates. You can change this anytime from the notification icon.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button size='small' color='inherit' onClick={() => setPromptOpen( false )} sx={{ fontSize: '12px' }}>
                        Not now
                    </Button>
                    <Button size='small' variant='contained' disableElevation onClick={handleEnableFromPrompt} disabled={push.busy} sx={{ fontSize: '12px' }}>
                        Enable
                    </Button>
                </DialogActions>
            </Dialog>

            {/* In-app notification dialog: pushes that arrive while the user is on the page */}
            <Dialog
                open={Boolean( activeNotification )}
                onClose={handleDismissInAppNotification}
                maxWidth='xs'
                fullWidth
                aria-labelledby='student-in-app-notification-title'
            >
                {activeNotification && (
                    <>
                        <DialogTitle
                            id='student-in-app-notification-title'
                            sx={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '15px', fontWeight: 'bold', pb: 1 }}
                        >
                            {Icons.default.NotificationsOutlined}
                            {activeNotification.title}
                        </DialogTitle>
                        <DialogContent sx={{ pb: 1 }}>
                            {activeNotification.body && (
                                <Typography variant='body2' sx={{ fontSize: '13px', color: 'text.secondary', lineHeight: 1.5, whiteSpace: 'pre-line' }}>
                                    {activeNotification.body}
                                </Typography>
                            )}
                            {inAppNotifications.length > 1 && (
                                <Typography variant='caption' sx={{ display: 'block', mt: 1.5, color: 'text.secondary' }}>
                                    {inAppNotifications.length - 1} more {inAppNotifications.length - 1 === 1 ? 'update' : 'updates'} waiting
                                </Typography>
                            )}
                        </DialogContent>
                        <DialogActions sx={{ px: 3, pb: 2 }}>
                            <Button size='small' variant='text' color='inherit' onClick={handleDismissInAppNotification} sx={{ fontSize: '12px' }}>
                                {inAppNotifications.length > 1 ? 'Next' : 'Dismiss'}
                            </Button>
                            {activeNotification.url && new URL( activeNotification.url, window.location.origin ).pathname !== '/' && (
                                <Button size='small' variant='contained' disableElevation onClick={handleViewInAppNotification} sx={{ fontSize: '12px' }}>
                                    View
                                </Button>
                            )}
                        </DialogActions>
                    </>
                )}
            </Dialog>

            {/* iOS: notifications need the app on the home screen, which can only be done from Safari's Share menu */}
            <Dialog open={installDialogOpen} onClose={() => setInstallDialogOpen( false )} maxWidth='xs' fullWidth>
                <DialogTitle sx={{ fontSize: '15px', fontWeight: 'bold', pb: 1 }}>Add to Home Screen</DialogTitle>
                <DialogContent sx={{ pb: 1 }}>
                    <Typography variant='body2' sx={{ fontSize: '13px', color: 'text.secondary', lineHeight: 1.5, mb: 1 }}>
                        On iPhone and iPad, notifications only work once the app is on your home screen:
                    </Typography>
                    <Typography component='ol' variant='body2' sx={{ fontSize: '13px', color: 'text.secondary', lineHeight: 1.7, pl: 2.5, m: 0 }}>
                        <li>Open this page in Safari.</li>
                        <li>Tap the Share button.</li>
                        <li>Choose <b>Add to Home Screen</b>, then tap Add.</li>
                        <li>Open the app from your home screen and enable notifications.</li>
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button size='small' variant='contained' disableElevation onClick={() => setInstallDialogOpen( false )} sx={{ fontSize: '12px' }}>
                        Got it
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default StudentPushNotifications
